export enum ConfigurationTypes {
  Coat = '1',
  TuxedoTrouser = '2',
  Waistcoat = '3',
  TuxedoShirt = '4',
  Trousers = '5',
  Shirt = '6',
  TuxedoJacket = '7',
  TuxedoWaistcoat = '8',
  Jacket = '9',
  ExtraTrouser = '105',
  ExtraTuxedoTrouser = '102',
}

export const productCodeByConfigurationId: { [key: number]: string } = {
  1: 'Coat',
  2: 'TuxedoTrouser',
  3: 'Waistcoat',
  4: 'TuxedoShirt',
  5: 'Trouser',
  6: 'Shirt',
  7: 'TuxedoJacket',
  8: 'TuxedoWaistcoat',
  9: 'Jacket',
  // All extra configurations have a configuration type id of 100 + base product configuration type id. These definitions exist only on the FE side.
  // Because  BE is not aware of extra configuration ids:
  // 1. Extra configurations read definitions from base configurations but have different configuration type id to make distinction in selection on the FE side
  // 2. We always pass the base configuration id to BE with isExtraProduct flag set to true
  // 3. BE will return the base configuration id in the response, we have to read isExtraProduct flag and check if given configuration id has a matching extra configuration id
  102: 'ExtraTuxedoTrouser',
  105: 'ExtraTrouser',
};

export const Products = {
  Coat: [ConfigurationTypes.Coat],
  TuxedoTrouser: [ConfigurationTypes.TuxedoTrouser],
  Waistcoat: [ConfigurationTypes.Waistcoat],
  TuxedoShirt: [ConfigurationTypes.TuxedoShirt],
  Trouser: [ConfigurationTypes.Trousers],
  Shirt: [ConfigurationTypes.Shirt],
  TuxedoJacket: [ConfigurationTypes.TuxedoJacket],
  TuxedoWaistcoat: [ConfigurationTypes.TuxedoWaistcoat],
  Jacket: [ConfigurationTypes.Jacket],
  Tuxedo: [
    ConfigurationTypes.TuxedoJacket,
    ConfigurationTypes.TuxedoTrouser,
    ConfigurationTypes.ExtraTuxedoTrouser,
    ConfigurationTypes.TuxedoWaistcoat,
  ],
  Suit: [
    ConfigurationTypes.Jacket,
    ConfigurationTypes.Trousers,
    ConfigurationTypes.ExtraTrouser,
    ConfigurationTypes.Waistcoat,
  ],
};

export enum MeasProductTypes {
  Jacket = 'Jacket',
  Belt = 'Belt',
  Socks = 'Socks',
  Trousers = 'Trousers',
  Waistcoat = 'Waistcoat',
  Shirt = 'Shirt',
  OuterwearFormal = 'OuterwearFormal',
  ShirtXp = 'ShirtXp',
  ShirtLetter = 'ShirtLetter',
  CasualShirt = 'CasualShirt',
  TShirt = 'TShirt',
  Knitwear = 'Knitwear',
  Jeans = 'Jeans',
  Shorts = 'Shorts',
  BodyWarmer = 'BodyWarmer',
  OuterwearCasual = 'OuterwearCasual',
  Shoe = 'Shoe',
  TrousersLeisure = 'TrousersLeisure',
  'Trousers-Jeans Sizes' = 'Trousers-Jeans Sizes',
}

export type ProductType = keyof typeof Products;

// OuterwearFormal is a MEAS alias for Coat
export type ProductTypeMeas = keyof typeof MeasProductTypes;

export const defaultProduct = 'Suit';

export const additionalConfigurationTypeByProduct = {
  Coat: [],
  TuxedoTrouser: [],
  Waistcoat: [],
  TuxedoShirt: [],
  Trouser: [],
  Shirt: [],
  TuxedoJacket: [],
  TuxedoWaistcoat: [],
  Jacket: [],
  Tuxedo: [ConfigurationTypes.TuxedoWaistcoat, ConfigurationTypes.ExtraTuxedoTrouser],
  Suit: [ConfigurationTypes.Waistcoat, ConfigurationTypes.ExtraTrouser],
};

export enum ProductMandatoryLayerSize {
  Coat = 6,
  TuxedoTrouser = 3,
  Waistcoat = 2,
  TuxedoShirt = 4,
  Trouser = 5, // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  Shirt = 5,
  TuxedoJacket = 7, // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  TuxedoWaistcoat = 2, // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  Jacket = 7,
}

// All extra configurations have a configuration type id of 100 + base product configuration type id. These definitions exist only on the FE side.
// Because  BE is not aware of extra configuration ids:
// 1. Extra configurations read definitions from base configurations but have different configuration type id to make distinction in selection on the FE side
// 2. We always pass the base configuration id to BE with isExtraProduct flag set to true
// 3. BE will return the base configuration id in the response, we have to read isExtraProduct flag and check if given configuration id has a matching extra configuration id
export const getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId = (extraConfigurationTypeId: number) => {
  if (extraConfigurationTypeId <= 100) {
    return extraConfigurationTypeId;
  }

  return extraConfigurationTypeId - 100;
};

export const rotationConfigurationTypes = [
  ConfigurationTypes.Trousers,
  ConfigurationTypes.ExtraTrouser,
  ConfigurationTypes.TuxedoTrouser,
  ConfigurationTypes.ExtraTuxedoTrouser,
];

// All extra configurations have a configuration type id of 100 + base product configuration type id. These definitions exist only on the FE side.
// Because  BE is not aware of extra configuration ids:
// 1. Extra configurations read definitions from base configurations but have different configuration type id to make distinction in selection on the FE side
// 2. We always pass the base configuration id to BE with isExtraProduct flag set to true
// 3. BE will return the base configuration id in the response, we have to read isExtraProduct flag and check if given configuration id has a matching extra configuration id
export const getExtraConfgurationByBaseConfiguration = (
  product: ProductType,
  configurationTypeId: ConfigurationTypes,
) => {
  if (!additionalConfigurationTypeByProduct[product] || additionalConfigurationTypeByProduct[product].length === 0) {
    return null;
  }

  return additionalConfigurationTypeByProduct[product].find(
    extraConfiguration => +extraConfiguration === +configurationTypeId + 100,
  );
};

/**
 * Resolves the ProductType the configurator should be opened in given the
 * configurationTypeIds the advisor picked on the /clone page.
 *
 * - Single-garment selection → maps the (extra-aware) configuration type id
 *   back to its single-garment ProductType (e.g. 9 → "Jacket", 5 / 105 →
 *   "Trouser").
 * - Multi-garment selection → returns the fallback (the source product, e.g.
 *   "Suit") so the configurator stays in its original combined product mode.
 *
 * Falls back to the source product when no exact match exists in
 * `productCodeByConfigurationId` / `Products`.
 */
export const getProductByConfigurationTypeIds = (
  configurationTypeIds: number[],
  fallbackProduct: ProductType,
): ProductType => {
  if (configurationTypeIds.length !== 1) {
    return fallbackProduct;
  }

  const baseId = getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(configurationTypeIds[0]);
  const productCode = productCodeByConfigurationId[baseId];

  if (productCode && productCode in Products) {
    return productCode as ProductType;
  }

  return fallbackProduct;
};
